import { message } from "antd";
import orderService from "../services/orders/index1";
import { CustomerService } from "../services/customers";

const nameSpace = "orders";

const initialState = {
  loading: {
    fetchList: false,
    fetch: false,
    updateStatus: false,
    create: false,
  },
  data: [],
  total: 0,
  limit: 10,
  error: null,
  currentOrder: null,
  history: {
    epoch: 0,
    list: [],
    total: 0,
    page: 1,
    loading: false,
    loadingMore: false,
    error: null,
    overview: null,
    before: null,
    overviewLoading: false,
    exporting: false,
  },
};

export const create = (data) => ({
  type: `${nameSpace}/create`,
  payload: data,
});

export const fetchList = (params) => ({
  type: `${nameSpace}/fetchList`,
  payload: params,
});

export const fetch = (id) => ({
  type: `${nameSpace}/fetch`,
  payload: id,
});

export const updateStatus = (id, status, driverId) => ({
  type: `${nameSpace}/updateStatus`,
  payload: { id, status, driverId },
});

// these return their result and leave the orders list alone
export const fetchOrder = (id) => ({
  type: `${nameSpace}/fetchOrder`,
  payload: id,
});

export const createBilling = (data) => ({
  type: `${nameSpace}/createBilling`,
  payload: data,
});

export const closeOrder = (id, data) => ({
  type: `${nameSpace}/closeOrder`,
  payload: { id, data },
});

export const updateList = (id) => ({
  type: `${nameSpace}/updateList`,
  id,
});

export const fetchActiveCarOrders = () => ({
  type: `${nameSpace}/fetchActiveCarOrders`,
});

export const addItemsToCarOrder = (data) => ({
  type: `${nameSpace}/addItemsToCarOrder`,
  payload: data,
});

export const updateCarOrderItem = (data) => ({
  type: `${nameSpace}/updateCarOrderItem`,
  payload: data,
});

export const updateCarOrderCar = (data) => ({
  type: `${nameSpace}/updateCarOrderCar`,
  payload: data,
});

export const removeCarOrderItem = (data) => ({
  type: `${nameSpace}/removeCarOrderItem`,
  payload: data,
});

export const fetchHistory = (params, quiet = false) => ({
  type: `${nameSpace}/fetchHistory`,
  payload: { params, quiet },
});

export const fetchHistoryMore = (params) => ({
  type: `${nameSpace}/fetchHistoryMore`,
  payload: { params },
});

export const fetchHistoryOverview = (
  params,
  compareParams = null,
  quiet = false,
) => ({
  type: `${nameSpace}/fetchHistoryOverview`,
  payload: { params, compareParams, quiet },
});

export const exportHistory = (params, { pageSize = 500, cap = 5000 } = {}) => ({
  type: `${nameSpace}/exportHistory`,
  payload: { params, pageSize, cap },
});

export const addOrderPayments = (id, payments) => ({
  type: `${nameSpace}/addOrderPayments`,
  payload: { id, payments },
});

export const cancelHistoryOrder = (id, body) => ({
  type: `${nameSpace}/cancelHistoryOrder`,
  payload: { id, body },
});

export const cancelHistoryItems = (id, body) => ({
  type: `${nameSpace}/cancelHistoryItems`,
  payload: { id, body },
});

export const fetchHistoryStaff = () => ({
  type: `${nameSpace}/fetchHistoryStaff`,
});

export const clearHistory = () => ({
  type: `${nameSpace}/clearHistory`,
});

export default {
  namespace: nameSpace,
  state: initialState,
  effects: {
    // newest request wins; quiet refreshes never touch loading/error
    fetchHistory: [
      function* ({ payload }, { call, put, select }) {
        const { params, quiet } = payload;
        const epochOf = (state) => state[nameSpace]?.history?.epoch;
        if (!quiet) {
          yield put({ type: "setHistoryLoading", payload: true });
          yield put({ type: "setHistoryError", payload: null });
        }
        try {
          const epoch = yield select(epochOf);
          const response = yield call(orderService.fetchHistory, params);
          // cleared (page left) or superseded meanwhile: drop these rows
          const now = yield select(epochOf);
          if (now !== epoch) return;
          yield put({
            type: "setHistory",
            payload: {
              list: response?.data?.list || [],
              total: response?.data?.total || 0,
              page: params?.page || 1,
            },
          });
        } catch (error) {
          if (!quiet) {
            yield put({
              type: "setHistoryError",
              payload: error.message || "Failed to load orders",
            });
          }
        } finally {
          if (!quiet) {
            yield put({ type: "setHistoryLoading", payload: false });
          }
        }
      },
      { type: "takeLatest" },
    ],
    *fetchHistoryMore({ payload }, { call, put, select }) {
      yield put({ type: "setHistoryLoadingMore", payload: true });
      const epochOf = (state) => state[nameSpace]?.history?.epoch;
      try {
        const epoch = yield select(epochOf);
        const response = yield call(orderService.fetchHistory, payload.params);
        // a new load or a clear happened meanwhile: these rows are stale
        const now = yield select(epochOf);
        if (now !== epoch) return;
        yield put({
          type: "appendHistory",
          payload: {
            list: response?.data?.list || [],
            total: response?.data?.total || 0,
            page: payload.params?.page,
          },
        });
      } catch (error) {
        message.error(error.message);
      } finally {
        yield put({ type: "setHistoryLoadingMore", payload: false });
      }
    },
    fetchHistoryOverview: [
      function* ({ payload }, { call, put }) {
        const { params, compareParams, quiet } = payload;
        if (!quiet) {
          yield put({ type: "setHistoryOverviewLoading", payload: true });
        }
        try {
          const response = yield call(orderService.fetchOverview, params);
          let before = null;
          if (compareParams) {
            try {
              const prev = yield call(
                orderService.fetchOverview,
                compareParams,
              );
              before = prev?.data || null;
            } catch (error) {
              before = null;
            }
          }
          yield put({
            type: "setHistoryOverview",
            payload: { overview: response?.data || null, before },
          });
        } catch (error) {
          yield put({
            type: "setHistoryOverview",
            payload: { overview: null, before: null },
          });
        } finally {
          if (!quiet) {
            yield put({ type: "setHistoryOverviewLoading", payload: false });
          }
        }
      },
      { type: "takeLatest" },
    ],
    // returns { list, total, truncated } (null when it failed)
    *exportHistory({ payload }, { call, put }) {
      const { params, pageSize, cap } = payload;
      yield put({ type: "setHistoryExporting", payload: true });
      try {
        const list = [];
        const seen = new Set();
        let total = 0;
        for (let page = 1; ; page += 1) {
          const response = yield call(orderService.fetchHistory, {
            ...params,
            page,
            limit: pageSize,
          });
          const rows = response?.data?.list || [];
          if (page === 1) total = response?.data?.total || 0;
          if (!rows.length) break;
          let added = 0;
          rows.forEach((row) => {
            if (!seen.has(row.id)) {
              seen.add(row.id);
              list.push(row);
              added += 1;
            }
          });
          // a page of rows already seen: the server ignores paging, stop
          if (!added) break;
          if (list.length >= Math.min(total, cap)) break;
        }
        return { list: list.slice(0, cap), total, truncated: total > cap };
      } catch (error) {
        message.error(error.message);
        return null;
      } finally {
        yield put({ type: "setHistoryExporting", payload: false });
      }
    },
    // returns [{ label, value }] of the shop's staff ([] when it failed)
    *fetchHistoryStaff(_, { call }) {
      try {
        const response = yield call(CustomerService.fetchList, {
          isStaff: true,
          page: 1,
          limit: 100,
        });
        return (response?.data?.list || [])
          .filter((u) => u && u.name)
          .map((u) => ({ label: u.name, value: u.id }));
      } catch (error) {
        return [];
      }
    },
    // returns the updated order (null when the payment was not saved)
    *addOrderPayments({ payload }, { call }) {
      try {
        const response = yield call(
          orderService.addPayments,
          payload.id,
          payload.payments,
        );
        if (!response?.data) {
          message.error(response?.error || "The payment was not saved.");
          return null;
        }
        return response.data;
      } catch (error) {
        message.error(
          error.response?.data?.error ||
            error.message ||
            "The payment was not saved.",
        );
        return null;
      }
    },
    // returns the updated order (null when nothing was saved)
    *cancelHistoryOrder({ payload }, { call }) {
      try {
        const response = yield call(
          orderService.cancelOrder,
          payload.id,
          payload.body,
        );
        if (!response?.data) {
          message.error(response?.error || "The order was not cancelled.");
          return null;
        }
        return response.data;
      } catch (error) {
        message.error(
          error.response?.data?.error ||
            error.message ||
            "The order was not cancelled.",
        );
        return null;
      }
    },
    // returns the updated order (null when nothing was saved)
    *cancelHistoryItems({ payload }, { call }) {
      try {
        const response = yield call(
          orderService.cancelOrderItems,
          payload.id,
          payload.body,
        );
        if (!response?.data) {
          message.error(response?.error || "The items were not cancelled.");
          return null;
        }
        return response.data;
      } catch (error) {
        message.error(
          error.response?.data?.error ||
            error.message ||
            "The items were not cancelled.",
        );
        return null;
      }
    },
    *updateList({ id }, { put }) {
      yield put({ type: "updateOrderStatus", payload: { id } });
    },
    // returns the saved order (null when it was not placed)
    *create({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "create" });
      let order = null;
      try {
        const response = yield call(orderService.create, payload);
        yield put({
          type: "setCurrentOrder",
          payload: response.data,
        });
        if (response.data) {
          order = response.data;
          message.success("Order placed successfully");
        }
      } catch (error) {
        message.error(error.message);
      } finally {
        yield put({ type: "stopLoading", loadingType: "create" });
      }
      return order;
    },
    *updateStatus({ payload }, { call, put }) {
      if (payload.status !== "cancelled") {
        yield put({ type: "startLoading", loadingType: "updateStatus" });
      }

      try {
        const response = yield call(orderService.updateStatus, payload.id, {
          status: payload.status,
          shop_driver_id: payload.driverId,
        });
        yield put({
          type: "updateOrderStatus",
          payload: response.data,
        });
      } catch (error) {
        message.error(error.message);
      } finally {
        yield put({ type: "stopLoading", loadingType: "updateStatus" });
      }
    },
    *fetch({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "fetch" });
      try {
        const response = yield call(orderService.fetch, payload);
        yield put({
          type: "addNewOrder",
          payload: response.data,
        });
      } catch (error) {
        message.error(error.message);
      } finally {
        yield put({ type: "stopLoading", loadingType: "fetch" });
      }
    },
    // returns the order (null when it could not be loaded)
    *fetchOrder({ payload }, { call }) {
      try {
        const response = yield call(orderService.fetch, payload);
        return response?.data || null;
      } catch (error) {
        return null;
      }
    },
    // returns { data, error } so the caller can show why the bill failed
    *createBilling({ payload }, { call }) {
      try {
        const response = yield call(orderService.createBilling, payload);
        return { data: response?.data || null, error: "" };
      } catch (error) {
        return { data: null, error: error.message || "" };
      }
    },
    // returns { data, error }; data is the closed order
    *closeOrder({ payload }, { call }) {
      try {
        const response = yield call(
          orderService.updateStatus,
          payload.id,
          payload.data,
        );
        if (!response?.data)
          return { data: null, error: response?.error || "" };
        return { data: response.data, error: "" };
      } catch (error) {
        return { data: null, error: error.message || "" };
      }
    },
    *fetchActiveCarOrders(_, { call }) {
      try {
        const response = yield call(orderService.fetchActiveCarOrders);
        return Array.isArray(response?.data) ? response.data : null;
      } catch (error) {
        // null: not loaded (keep the list this device already has)
        return null;
      }
    },
    *addItemsToCarOrder({ payload }, { call }) {
      try {
        const response = yield call(orderService.addItemsToCarOrder, payload);
        if (response?.data) {
          message.success("Items added to car order successfully");
          return response.data;
        }
        return null;
      } catch (error) {
        message.error(error.message || "Failed to add items");
        return null;
      }
    },
    *updateCarOrderItem({ payload }, { call }) {
      try {
        const response = yield call(orderService.updateCarOrderItem, payload);
        if (response?.data) {
          // a void (reason set) gets its own "n× voided" toast from the drawer
          if (!payload?.reason) message.success("Car order updated successfully");
          return response.data;
        }
        return null;
      } catch (error) {
        message.error(error.message || "Failed to update item");
        return null;
      }
    },
    *updateCarOrderCar({ payload }, { call }) {
      try {
        const response = yield call(orderService.updateCarOrderCar, payload);
        if (response?.data) {
          message.success("Car details updated");
          return response.data;
        }
        return null;
      } catch (error) {
        message.error(error.message || "Failed to update car details");
        return null;
      }
    },
    *removeCarOrderItem({ payload }, { call }) {
      try {
        const response = yield call(orderService.removeCarOrderItem, payload);
        if (response?.data) {
          message.success("Item removed from car order");
          return response.data;
        }
        return null;
      } catch (error) {
        message.error(error.message || "Failed to remove item");
        return null;
      }
    },
    *fetchList({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "fetchList" });
      try {
        const response = yield call(orderService.fetchList, payload);
        yield put({
          type: "setOrders",
          payload: { ...response.data, page: payload.page },
        });
      } catch (error) {
        yield put({ type: "setError", payload: error });
      } finally {
        yield put({ type: "stopLoading", loadingType: "fetchList" });
      }
    },
  },
  reducers: {
    setHistory(state, { payload }) {
      return {
        ...state,
        history: {
          ...state.history,
          epoch: state.history.epoch + 1,
          list: payload.list,
          total: payload.total,
          page: payload.page || 1,
        },
      };
    },
    appendHistory(state, { payload }) {
      const seen = new Set(state.history.list.map((o) => o.id));
      const fresh = payload.list.filter((o) => !seen.has(o.id));
      return {
        ...state,
        history: {
          ...state.history,
          list: [...state.history.list, ...fresh],
          total: payload.total,
          page: payload.page || state.history.page,
        },
      };
    },
    setHistoryError(state, { payload }) {
      return { ...state, history: { ...state.history, error: payload } };
    },
    setHistoryLoading(state, { payload }) {
      // a non-quiet load starting invalidates any "show more" in flight
      const epoch = state.history.epoch + (payload ? 1 : 0);
      return {
        ...state,
        history: { ...state.history, loading: payload, epoch },
      };
    },
    setHistoryLoadingMore(state, { payload }) {
      return { ...state, history: { ...state.history, loadingMore: payload } };
    },
    setHistoryOverviewLoading(state, { payload }) {
      return {
        ...state,
        history: { ...state.history, overviewLoading: payload },
      };
    },
    setHistoryExporting(state, { payload }) {
      return { ...state, history: { ...state.history, exporting: payload } };
    },
    setHistoryOverview(state, { payload }) {
      return {
        ...state,
        history: {
          ...state.history,
          overview: payload.overview,
          before: payload.before,
        },
      };
    },
    clearHistory(state) {
      return {
        ...state,
        history: {
          ...initialState.history,
          epoch: state.history.epoch + 1,
        },
      };
    },
    setCurrentOrder(state, { payload }) {
      return { ...state, currentOrder: payload };
    },
    startLoading(state, { loadingType }) {
      return { ...state, loading: { ...state.loading, [loadingType]: true } };
    },
    stopLoading(state, { loadingType }) {
      return { ...state, loading: { ...state.loading, [loadingType]: false } };
    },
    setOrders(state, { payload }) {
      if (payload.page && payload.page > 1) {
        return {
          ...state,
          data: [...state.data, ...payload.list],
          total: payload.total,
        };
      } else {
        return {
          ...state,
          data: payload.list,
          total: payload.total,
        };
      }
    },
    addNewOrder(state, { payload }) {
      const exists = state.data.some((order) => order.id === payload.id);

      return {
        ...state,
        data: !exists
          ? payload.status === "confirmed"
            ? [...state.data, payload]
            : [payload, ...state.data]
          : state.data,
        total: !exists ? state.total + 1 : state.total,
      };
    },
    updateOrderStatus(state, { payload }) {
      return {
        ...state,
        data: state.data.filter((order) => order.id !== payload.id),
        total: state.total - 1,
      };
    },
    setError(state, { payload }) {
      return { ...state, error: payload };
    },
  },
};
